import { useRef, useEffect } from "react";
import { useTheme } from "styled-components";
import useIsMobile from "../../hooks/useIsMobile";
import { Canvas } from "./Starscape.styles";

export default function Starscape() {
  const canvasRef = useRef(null);
  const theme = useTheme();
  const isLight = theme.light;
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return;

    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const stars = [];

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    function resize() {
      c.width = window.innerWidth * dpr;
      c.height = window.innerHeight * dpr;
      c.style.width = window.innerWidth + "px";
      c.style.height = window.innerHeight + "px";
    }
    resize();
    window.addEventListener("resize", resize);

    const STAR_COUNT = 120;
    for (let i = 0; i < STAR_COUNT; i++) {
      stars.push({
        x: Math.random() * c.width,
        y: Math.random() * c.height,
        radius: Math.random() * 1.2 + 0.3,
        baseOpacity: Math.random() * 0.6 + 0.2,
        driftX: (Math.random() - 0.5) * 0.03,
        driftY: (Math.random() - 0.5) * 0.02,
        twinkleSpeed: Math.random() * 0.002 + 0.001,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }

    let animId;
    let visible = !document.hidden;
    const onVisChange = () => {
      visible = !document.hidden;
      if (visible && !animId) animate();
    };
    document.addEventListener("visibilitychange", onVisChange);

    function animate() {
      if (!visible) {
        animId = null;
        return;
      }
      ctx.clearRect(0, 0, c.width, c.height);
      stars.forEach((s) => {
        s.x += s.driftX;
        s.y += s.driftY;
        if (s.x < 0) s.x = c.width;
        if (s.x > c.width) s.x = 0;
        if (s.y < 0) s.y = c.height;
        if (s.y > c.height) s.y = 0;
        const tw = Math.sin(Date.now() * s.twinkleSpeed + s.twinklePhase);
        const op = Math.max(0, s.baseOpacity + tw * 0.3);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
        ctx.fillStyle = isLight
          ? `rgba(0,0,0,${op * 0.25})`
          : `rgba(255,255,255,${op})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisChange);
      if (animId) cancelAnimationFrame(animId);
    };
  }, [isLight, isMobile]);

  if (isMobile) return null;

  return <Canvas ref={canvasRef} />;
}
